/**
 * KioskLogs — Core (kiosk) debug logs for a machine.
 *
 * Reads GET /api/agent/logs?id=N (kiosk_logs table). Lines are shipped from
 * the kiosk's C:\MyCopy\logs\core-*.log by the MyCopyLogShip scheduled task.
 * Newest-first; auto-refreshes every 15s.
 */

import { useEffect, useState, useCallback } from 'react';
import { getKioskLogs, type KioskLog } from '../api/client';

export function KioskLogs({ machineId }: { machineId: number }) {
  const [logs, setLogs] = useState<KioskLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getKioskLogs(machineId)
      .then((l) => {
        setLogs(l);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load logs'))
      .finally(() => setLoading(false));
  }, [machineId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="card mb-6">
      <div className="card-header flex items-center justify-between" style={{ gap: 8 }}>
        <span>Kiosk Logs (Core) {logs.length > 0 ? `· ${logs.length}` : ''}</span>
        <button className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: '0.8rem' }} onClick={load} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </button>
      </div>
      <div className="card-body">
        {error && <p className="text-secondary">Error: {error}</p>}
        {!error && logs.length === 0 && (
          <div className="empty-state">
            <p className="text-muted">No kiosk Core logs yet. The kiosk ships them every ~2 min.</p>
          </div>
        )}
        {logs.length > 0 && (
          <pre
            style={{
              maxHeight: 380,
              overflow: 'auto',
              fontSize: '0.78rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            }}
          >
            {logs.map((l) => l.message).join('\n')}
          </pre>
        )}
      </div>
    </div>
  );
}
